class AlgoliaCurrentRefinements extends HTMLElement {
  constructor() {
    super();

    this.connectedCallback = this.connectedCallback.bind(this);
    this.createDataset = this.createDataset.bind(this);
    this.createWidget = this.createWidget.bind(this);
    this.getDataset = this.getDataset.bind(this);
    this.getLabel = this.getLabel.bind(this);
    this.render = this.render.bind(this);
    this.renderContainer = this.renderContainer.bind(this);
    this.renderContent = this.renderContent.bind(this);
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    this.settings = {
      clearFilter: this.getAttribute('clear-filter'),
      filterPillFontSize: this.getAttribute('filter-pill-font-size')
    }
  }

  /**
   * Returns a formatted dataset based on the refinement.
   * @param {Object} refinement - The refinement object.
   * @returns {string} A formatted dataset based on the refinement.
   */
  createDataset(refinement) {
    const dataset = Object.keys(refinement).map(key => `data-${key}="${refinement[key]}"`).join(' ');
    return dataset;
  }

  /**
   * Creates an instance of the current refinements widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} instances - Instances object containing algolia instances.
   * @returns {Object} The created current refinements widget instance.
   */
  createWidget(configuration, instances) {
    const { connectCurrentRefinements } = instances.algoliaShopify.externals.connectors;
    const connectedCurrentRefinements = connectCurrentRefinements((renderOptions, isFirstRender) => this.render(configuration, instances, isFirstRender, renderOptions));

    const selector = `.${configuration.container}`;
    const container = document.querySelector(selector);

    const currentRefinementsWidget = connectedCurrentRefinements({ container });
    return currentRefinementsWidget;
  }

  /**
   * Returns a formatted dataset based on the event.
   * @param {Event} event - The event object.
   * @returns {string} A formatted dataset based on the event.
   */
  getDataset(event) {
    const dataset = Object.keys(event.currentTarget.dataset).reduce((previous, current) => ({ ...previous, [current]: event.currentTarget.dataset[current] }), {});
    return dataset;
  }

  /**
   * Returns a formatted message based on the refinement.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} instances - Instances object containing algolia instances.
   * @param {Object} refinement - The refinement object.
   * @returns {string} A formatted message based on the refinement.
   */
  getLabel(configuration, instances, refinement) {
    const facets = instances.algoliaShopify?.facets || [];

    const facet = facets.find(facet => facet.name === refinement.attribute);
    if (!facet) return refinement.label;

    if (facet.title.toLowerCase() === 'size' || configuration.isMobile) return `${facet.title} ${refinement.label}`;

    return refinement.label;
  }

  /**
   * Renders the current refinements widget with the provided render options.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} instances - Instances object containing algolia instances.
   * @param {boolean} isFirstRender - Indicates whether this is the first render.
   * @param {Object} renderOptions - Options for rendering the widget.
   * @returns {void}
   */
  render(configuration, instances, isFirstRender, renderOptions) {
    if (isFirstRender) return;

    const { canRefine, items, refine, widgetParams } = renderOptions;
    const { container } = widgetParams;

    container.classList.toggle('hidden', !canRefine);

    const content = items.map(item => item.refinements.map(refinement => this.renderContent(configuration, instances, refinement)));
    const refinementsDiv = document.createElement('div');
    refinementsDiv.classList.add('ais-current-refinements');
    //need to clear out the container's children or it will start duplicating options when using the back button
    container.innerHTML = '';

    container.appendChild(refinementsDiv);

    content.forEach(item => item.forEach(el => container.appendChild(el)));

    const anchors = container.querySelectorAll('a');
    if (anchors) anchors.forEach(anchor => anchor.addEventListener('click', (event) => {
      event.preventDefault();
      refine(this.getDataset(event));
    }));
  }

  /**
   * Renders the container for the current refinements widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Function} html - The HTML template function.
   * @returns {string} The rendered container.
   */
  renderContainer(configuration, html) {
    return html`<div class="${configuration.container} active-facets"></div>`;
  }

  /**
   * Renders the content.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} instances - Instances object containing algolia instances.
   * @param {Object} refinement - The refinement object containing information about the refinement.
   * @returns {string} The rendered content HTML.
   */
  renderContent(configuration, instances, refinement) {
    const label = this.getLabel(configuration, instances, refinement);
    const iconCloseSmall = this.querySelector('algolia-icon-close-small');
    const iconCloseSmallContent = iconCloseSmall ? iconCloseSmall.render() : '';

    const facetRemoveElement = document.createElement('facet-remove');
    
    const linkElement = document.createElement('a');
    linkElement.classList.add('active-facets__button', 'active-facets__button--light', 'filter-pill', `${configuration.filterPillFontSize}`);
    linkElement.setAttribute('href', '');
    
    Object.keys(refinement).forEach(key=> {
      if (typeof refinement[key] === 'string') {
        linkElement.dataset[key] = refinement[key].replaceAll('"', '').replaceAll("(", "").replaceAll(")", "");
      }
      else linkElement.dataset[key] = `${refinement[key]}`
    });
    if (iconCloseSmallContent) linkElement.appendChild(iconCloseSmallContent);

    const clearFilterSpan = document.createElement('span');
    clearFilterSpan.classList.add('visually-hidden');
    clearFilterSpan.textContent = configuration.clearFilter;

    const activeFacetsSpan = document.createElement('span');
    activeFacetsSpan.classList.add('active-facets__button-inner');
    activeFacetsSpan.textContent = label.replaceAll('"', '').replaceAll("(", "").replaceAll(")", "");

    linkElement.appendChild(clearFilterSpan);
    linkElement.appendChild(activeFacetsSpan);
    facetRemoveElement.appendChild(linkElement);

    return facetRemoveElement;
  }
}

customElements.define("algolia-current-refinements", AlgoliaCurrentRefinements);
